
import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown, Grid, Header, Icon, Ref, Segment } from 'semantic-ui-react'
import userDashboardActions from 'actions/user_dashboard';
import { GroupedBarChart, USER_PERMISSIONS } from 'dyl-components';
import { Chart } from 'dyl-components';
import { useNavigate } from 'react-router-dom';
import RestrictedView from 'shared/RestrictedView';

const useContainerDimensions = (defaultWidth = 800, defaultHeight = 500) => {
    const [dimensions, setDimensions] = useState({ width: defaultWidth, height: defaultHeight });
    const measuredRef = useCallback(node => {
        if (node !== null) {
            const resizeObserver = new ResizeObserver(entries => {
                const { width } = entries[0].contentRect;
                const height = width * 0.625; // 16:10 aspect ratio
                setDimensions({ width, height });
            });
            resizeObserver.observe(node);
            return () => resizeObserver.disconnect();
        }
    }, []);
    return [measuredRef, dimensions];
};

export default function PipelineCharts({ startTime, endTime }) {
    const [selectedLeadPipeline, setSelectedLeadPipeline] = useState(null);
    const [selectedOpportunityPipeline, setSelectedOpportunityPipeline] = useState(null);
    const { categories, categoriesForOpportunity, isReadingCategoriesForOpportunity, isReadingCategories } = useSelector((state) => state.pipeline);
    const { isReadingPipelineReport, pipeline_report } = useSelector((state) => state.user_dashboard);
    const leadPipelineData = useMemo(() => {
        if (pipeline_report) {
            return pipeline_report[selectedLeadPipeline]?.map((report) => {
                return {
                    x: report.stage_name,
                    y: report.count
                }
            });
        }
        return [];
    }, [pipeline_report, selectedLeadPipeline])
    const opportunityPipelineData = useMemo(() => {
        if (pipeline_report) {
            return pipeline_report[selectedOpportunityPipeline]?.map((report) => {
                return {
                    x: report.stage_name,
                    y: report.count
                }
            });
        }
        return [];
    }, [pipeline_report, selectedOpportunityPipeline])

    const [leadRef, { width: leadWidth, height: leadHeight }] = useContainerDimensions();
    const [opportunityRef, { width: opportunityWidth, height: opportunityHeight }] = useContainerDimensions();

    const margin = { top: 25, right: 10, bottom: 25, left: 40 };
    const dispatch = useDispatch();
    useEffect(() => {
        if (selectedLeadPipeline && selectedOpportunityPipeline) {
            dispatch(userDashboardActions.getPipelineReport({
                pipeline_id: [selectedLeadPipeline, selectedOpportunityPipeline].join(","),
                start_time: startTime,
                end_time: endTime
            }));
        }
    }, [selectedLeadPipeline, selectedOpportunityPipeline, dispatch, startTime, endTime])
    const handleLeadPipelineChange = (e, data) => {
        setSelectedLeadPipeline(data.value);
    }
    const handleOpportunityPipelineChange = (e, data) => {
        setSelectedOpportunityPipeline(data.value);
    }
    useEffect(() => {
        if (categories?.length > 0) {
            setSelectedLeadPipeline(categories[0].id);
        }
        if (categoriesForOpportunity?.length > 0) {
            setSelectedOpportunityPipeline(categoriesForOpportunity[0].id);
        }
    }, [categories, categoriesForOpportunity]);

    const maxBarWidth = 25;
    const navigate = useNavigate();
    return (
        <Grid>
            <Grid.Row columns={3}>
                <Grid.Column>
                    <Ref innerRef={leadRef}>
                        <Segment size='tiny' loading={isReadingCategories || isReadingPipelineReport} className='Barchart'>
                            <div className='Barchart__Header'>
                                <Header as="h3" color="primary" className='Barchart__Header--Left'>Lead Pipeline</Header>
                                <RestrictedView type="link" userPermission={USER_PERMISSIONS.PIPELINES_VIEW}>
                                    <Header as="h4" color="primary" className='Barchart__Header--Right' onClick={() => navigate(`/pipeline-reports?module=lead&pipeline_id=${selectedLeadPipeline}`)}><Icon className={"fas fa-file-chart-column"} size='small' />Advanced View</Header>
                                </RestrictedView>
                            </div>
                                <Dropdown
                                    placeholder='Select Pipeline'
                                    selection
                                    value={selectedLeadPipeline}
                                    options={categories?.map((category) => ({
                                    key: category.id,
                                    text: category.name,
                                    value: category.id,
                                }))}
                                onChange={handleLeadPipelineChange}
                            />
                            <Chart width={leadWidth} height={leadHeight}>
                                {!isReadingPipelineReport && <GroupedBarChart
                                    data={leadPipelineData}
                                    width={leadWidth}
                                    height={leadHeight}
                                    margin={margin}
                                    colors={"#10C6B4"}
                                    onMouseOver={(data) => console.log('Hover:', data)}
                                    additionalYTooltipLabel="Contacts"
                                    maxBarWidth={maxBarWidth}
                                />}
                            </Chart>
                        </Segment>
                    </Ref>
                </Grid.Column>
                <Grid.Column>
                    <Ref innerRef={opportunityRef}>
                        <Segment loading={isReadingCategoriesForOpportunity || isReadingPipelineReport} className='BarChart'>
                            <div className='Barchart__Header'>
                                <Header as="h3" color="primary" className='Barchart__Header--Left' >Opportunity Pipeline</Header>
                                <RestrictedView type="link" userPermission={USER_PERMISSIONS.PIPELINES_VIEW}>
                                    <Header as="h4" color="primary" className='Barchart__Header--Right' onClick={() => navigate(`/pipeline-reports?module=opportunity&pipeline_id=${selectedOpportunityPipeline}`)}><Icon className={"fas fa-file-chart-column"} size='small' />Advanced View</Header>
                                </RestrictedView>
                            </div>
                            <Dropdown
                                placeholder='Select Pipeline'
                                selection
                                value={selectedOpportunityPipeline}
                                options={categoriesForOpportunity?.map((category) => ({
                                    key: category.id,
                                    text: category.name,
                                    value: category.id,
                                }))}
                                onChange={handleOpportunityPipelineChange}
                            />
                            <Chart width={opportunityWidth} height={opportunityHeight}>
                                {!isReadingPipelineReport && <GroupedBarChart
                                    data={opportunityPipelineData}
                                    width={opportunityWidth}
                                    height={opportunityHeight}
                                    margin={margin}
                                    colors={"#10C6B4"}
                                    onMouseOver={(data) => console.log('Hover:', data)}
                                    additionalYTooltipLabel="Contacts"
                                    maxBarWidth={maxBarWidth}
                                />}
                            </Chart>
                        </Segment>
                    </Ref>
                </Grid.Column>
            </Grid.Row>
        </Grid>
    )
}
